/**
 * P3 — persistent Mission Progress Header. Mounted once in OperateLayout,
 * visible on every Operate tab. Drives the rail from:
 *   - incidents without a code change → AI analysis + runbook progress
 *     (the same rail whatever the mission type)
 *   - code-change incidents / requests → EI InvestigationState
 */
import { createPortal } from 'react-dom';
import React, { useCallback, useLayoutEffect, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Box,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Eye,
  Hexagon,
  Loader2,
  MoreHorizontal,
  Play,
  RotateCw,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { StatusTone } from '../../../design-system/v2';
import './mission-header-aurora.css';
import { useOperateWorkspace } from '../operate/OperateWorkspaceContext';
import { useLatestEiRun, useLatestEiRunForChangeRequest } from '../../../services/engineeringIntelligence';
import { amsQueryKeys, useAnalyzeAmsIncident, useRcaOwnership } from '../../../services/api';
import { useEngineeringRequestMission } from '../../../lib/useEngineeringRequestMission';
import { eiStateLabel, operatorPhaseLabel } from '../../../lib/eiLifecycle';
import { buildBlastRadiusVm } from '../../../lib/eiBlastRadius';
import {
  isEiGatheringState,
  latestEiToolActivity,
} from '../../../lib/eiToolActivity';
import { classifyMission } from '../../../lib/missionClassification';
import type { EiToolInvocation } from '../../../types/engineeringIntelligence';
import {
  markOpsMissionRerun,
  clearOpsMissionRerun,
  opsMissionRerunAgeMs,
  useOpsMissionRerunActive,
} from '../../../lib/opsHumanAttestation';
import {
  appendOpsActivity,
} from '../../../lib/opsActivityLog';
import {
  buildMissionHeaderStagesFromHimitsu,
  missionHeaderNextStage,
  missionHeaderProgressPercent,
  missionHeaderOperatorCue,
  extractMissionRiskTier,
  missionRiskTierWithBlastRadius,
  type MissionHeaderStage,
  type MissionHeaderStageId,
  type MissionHeaderRiskTier,
} from '../../../lib/missionHeaderStages';
import {
  operateTabPath,
  operateContextPath,
  operateEngineeringContextPath,
  operateExecutionsContextPath,
} from './projectPhases';
import { useEngineeringMissionStatus } from '../../../services/missionStatus';
import { buildMissionStatusSummary } from '../../../lib/missionStatusDetails';
import { extractAlertAnalysis } from '../../../lib/alertAnalysis';
import { useIncidentRunbook } from '../../../services/runbook';
import {
  buildIncidentStages,
  engineeringTabApplies,
  incidentNeedsEngineering,
  incidentNeedsYou,
  incidentStageSentence,
  pendingOperatorStep,
} from '../../../lib/incidentStages';
import { isEiEscalatedForIncident } from '../../../lib/eiOpsEscalation';
import { EnsMark } from '../../common/EnsLoader';


/** Severity mapping (GUIDELINES §4): critical/high → error, medium → warning, low → info. */
const RISK_TONE: Record<MissionHeaderRiskTier, StatusTone> = {
  low: 'info',
  medium: 'warning',
  high: 'error',
  critical: 'error',
};

function stageHref(
  id: MissionHeaderStageId,
  projectId: string,
  incidentId: string | null,
  requestId: string | null,
  engineeringContext: ReturnType<typeof useOperateWorkspace>['engineeringContext'],
): string {
  switch (id) {
    case 'entry':
    case 'investigation':
      return requestId ? operateTabPath(projectId, 'requests') : operateTabPath(projectId, 'incidents', incidentId);
    case 'engineering':
      return operateEngineeringContextPath(projectId, engineeringContext);
    case 'execution':
      return operateExecutionsContextPath(projectId, engineeringContext);
    case 'testing':
      return engineeringContext?.kind === 'request'
        ? operateContextPath(projectId, 'test-environment', engineeringContext)
        : operateTabPath(projectId, 'test-environment', incidentId);
    case 'learning':
      return engineeringContext?.kind === 'request'
        ? operateContextPath(projectId, 'learning', engineeringContext)
        : operateTabPath(projectId, 'learning', incidentId);
    default:
      return operateTabPath(projectId, 'incidents');
  }
}

/** Natural-language activity line — never "AI is package ready". */
function activityLine(opts: {
  operational: boolean;
  recoveryLabel?: string;
  recoveryMessage?: string;
  eiState?: string | null;
  toolTrace?: EiToolInvocation[] | null;
}): string | null {
  if (opts.operational) {
    if (opts.recoveryMessage) return opts.recoveryMessage;
    if (opts.recoveryLabel) return opts.recoveryLabel;
    return null;
  }
  const phase = operatorPhaseLabel(opts.eiState);
  const s = String(opts.eiState || '').toUpperCase();
  if (!s) return null;
  if (s === 'NEEDS_HUMAN') {
    // Don't say "Execution paused" — the header already shows the Needs-you cue,
    // and progress can already be 100% while a closing decision is still open.
    return null;
  }
  if (s === 'AWAITING_APPROVAL' || s === 'IMPLEMENTATION_PACKAGE_READY') {
    return 'Implementation package ready — awaiting approval';
  }
  if (s === 'ENGINEERING_DESIGN' || s === 'PLANNING') {
    return 'AI is designing the remediation package';
  }
  if (s === 'EXECUTING') return 'AI is executing the approved package';
  if (s === 'VALIDATING') return 'AI is validating recovery';
  if (['RESOLVED', 'CLOSED', 'LEARNING', 'KNOWLEDGE_UPDATE'].includes(s)) {
    return 'Mission complete';
  }
  if (isEiGatheringState(s)) {
    const tool = latestEiToolActivity(opts.toolTrace);
    if (tool) return tool;
    return 'Gathering more evidence';
  }
  if (phase) return `AI is ${phase.toLowerCase()}`;
  return null;
}

const CARD_TONE: Record<StatusTone, string> = {
  success: 'var(--ops-ok)',
  info: 'var(--ops-cyan)',
  warning: 'var(--ops-warn)',
  error: 'var(--ops-danger)',
  neutral: 'var(--ops-muted)',
} as Record<StatusTone, string>;

const PILL_TONE: Record<StatusTone, string> = {
  success: 'ok',
  info: 'cyan',
  warning: 'warn',
  error: 'danger',
  neutral: 'neutral',
} as Record<StatusTone, string>;

const STAGE_STATUS_LABEL: Record<MissionHeaderStage['status'], string> = {
  completed: 'done',
  active: 'in progress',
  blocked: 'held up',
  pending: 'not started',
};

/**
 * Current-stage pill that opens the full stage list: done, current (running
 * or held up), not started. Each stage links to the tab that owns it.
 */
const StageFlow: React.FC<{
  stages: MissionHeaderStage[];
  focus: MissionHeaderStage | null;
  doneCount: number;
  heldFailure: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  hrefFor: (id: MissionHeaderStageId) => string;
  popRef: React.RefObject<HTMLDivElement>;
}> = ({ stages, focus, doneCount, heldFailure, open, onToggle, onClose, hrefFor, popRef }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const close = () => {
    onClose();
    btnRef.current?.focus();
  };
  // Rendered on <body> at a fixed position so no clipping or stacking ancestor can hide it.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(300, window.innerWidth - 40);
      setPos({ top: r.bottom + 8, left: Math.max(20, Math.min(r.left, window.innerWidth - width - 20)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  const allDone = stages.length > 0 && doneCount === stages.length;
  const label = allDone ? 'All stages complete' : focus?.label ?? 'Not started';
  return (
    <span className="ops-stage-wrap">
      <button
        ref={btnRef}
        type="button"
        className={`ops-pill stage${allDone ? ' ok' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="mission-flow-pop"
        title={`Current stage: ${label} (${doneCount} of ${stages.length} complete)`}
        onClick={onToggle}
      >
        <span className="ops-ico" aria-hidden>
          {allDone ? <Check size={11} strokeWidth={2.6} /> : <i />}
        </span>
        {label}
        <span className="ops-step">
          {doneCount}/{stages.length}
        </span>
        <ChevronDown className="ops-chev" size={12} strokeWidth={2.4} aria-hidden />
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          className="mph-v2 ops-menu ops-flow"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1400 }}
          id="mission-flow-pop"
          role="dialog"
          aria-labelledby="mission-flow-title"
          onKeyDown={(e) => e.key === 'Escape' && close()}
        >
          <div className="ops-flow-head">
            <div>
              <strong id="mission-flow-title">Mission progress</strong>
              <span>
                {doneCount} of {stages.length} stages complete
              </span>
            </div>
            <button type="button" className="ops-flow-close" aria-label="Close progress" onClick={close}>
              <X size={14} />
            </button>
          </div>
          <ol className="ops-flow-list">
            {stages.map((stage) => {
              const current = stage.status === 'active' || stage.status === 'blocked';
              const cls =
                stage.status === 'completed' ? 'done' : current ? `now${stage.status === 'blocked' ? ' held' : ''}${heldFailure && stage.status === 'blocked' ? ' fail' : ''}` : '';
              return (
                <li key={stage.id} className={cls} aria-current={current ? 'step' : undefined}>
                  <RouterLink to={hrefFor(stage.id)} onClick={onClose} title={`${stage.label} — ${STAGE_STATUS_LABEL[stage.status]}`}>
                    <span className="mk" aria-hidden>
                      {stage.status === 'completed' ? (
                        <Check size={11} strokeWidth={2.6} />
                      ) : heldFailure && stage.status === 'blocked' ? (
                        <X size={11} strokeWidth={2.6} />
                      ) : current ? (
                        <i />
                      ) : null}
                    </span>
                    {stage.label}
                    {current && <em>{stage.status === 'blocked' ? (heldFailure ? 'Failed' : 'Held up') : 'Current'}</em>}
                  </RouterLink>
                </li>
              );
            })}
          </ol>
        </div>,
        document.body,
      )}
    </span>
  );
};

const MissionProgressHeader: React.FC = () => {
  const { projectId, incidentId, requestId, engineeringContext, incident, decision, missionChromeActions: chrome } =
    useOperateWorkspace();

  const incidentRunQ = useLatestEiRun(projectId, engineeringContext?.kind === 'incident' ? incidentId : undefined);
  const { crId: requestCrId, request: engineeringRequest } = useEngineeringRequestMission(
    projectId,
    engineeringContext?.kind === 'request' ? requestId ?? undefined : undefined,
  );
  const requestRunQ = useLatestEiRunForChangeRequest(
    projectId,
    engineeringContext?.kind === 'request' ? requestCrId : undefined,
  );
  const run = engineeringContext?.kind === 'request' ? requestRunQ.data : incidentRunQ.data;
  const statusTarget = useMemo(
    () =>
      engineeringContext?.kind === 'request'
        ? { crId: requestCrId }
        : { incidentId: engineeringContext?.kind === 'incident' ? incidentId : null },
    [engineeringContext?.kind, requestCrId, incidentId],
  );
  const missionStatusQ = useEngineeringMissionStatus(projectId, statusTarget, {
    enabled: Boolean(engineeringContext),
  });


  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const analyze = useAnalyzeAmsIncident(projectId);
  const [rerunBusy, setRerunBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<HTMLDivElement>(null);
  const flowPopRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen && !flowOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(t)) setMenuOpen(false);
      if (flowRef.current && !flowRef.current.contains(t) && !flowPopRef.current?.contains(t)) setFlowOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      setFlowOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, flowOpen]);
  const rerunActive = useOpsMissionRerunActive(projectId, incidentId);

  const classification = useMemo(
    () => classifyMission({ incident, decision }),
    [incident, decision],
  );
  // One rail for every incident without a code change: analysis + runbook progress.
  const alertAnalysis = useMemo(() => extractAlertAnalysis(incident), [incident]);
  const runbookQ = useIncidentRunbook(projectId, engineeringContext?.kind === 'incident' ? incidentId : null);
  // Ownership the user confirmed as ENGINEERING puts the incident on the engineering rail.
  const engineeringOwned =
    useRcaOwnership(projectId, engineeringContext?.kind === 'incident' ? incidentId : null).data?.final_owner === 'ENGINEERING';
  const incidentClosed =
    String(incident?.status || '').toLowerCase() === 'closed' || Boolean(incident?.closed_at);

  const onRerunMission = useCallback(async () => {
    if (!incidentId) return;
    setRerunBusy(true);
    try {
      markOpsMissionRerun(projectId, incidentId);
      appendOpsActivity(
        projectId,
        incidentId,
        'Rerun mission — clearing stale close UX and re-analyzing…',
        'rerun',
      );
      appendOpsActivity(projectId, incidentId, 'Analyze started', 'analyze');
      await analyze.mutateAsync(incidentId);
      appendOpsActivity(projectId, incidentId, 'Analyze complete — reassessment cycle finished', 'analyze');
      await queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'ams'] });
      // The RCA ownership key is not under ['projects', id, 'ams'].
      void queryClient.invalidateQueries({ queryKey: amsQueryKeys.rcaOwnership(projectId, incidentId) });
      navigate(operateTabPath(projectId, 'engineering', incidentId));
    } catch {
      appendOpsActivity(
        projectId,
        incidentId,
        'Analyze failed — clearing rerun grace; natural recovery assessment applies',
        'analyze',
      );
      navigate(operateTabPath(projectId, 'engineering', incidentId));
    } finally {
      // End rerun grace so Needs you only appears from natural lab-blocked / awaiting assessment.
      clearOpsMissionRerun(projectId, incidentId);
      setRerunBusy(false);
    }
  }, [analyze, incidentId, navigate, projectId, queryClient]);

  // Clear orphaned rerun grace left by HMR / crashed mid-analyze (not an in-flight click).
  useEffect(() => {
    if (!projectId || !incidentId || !rerunActive || rerunBusy || analyze.isPending) return;
    const age = opsMissionRerunAgeMs(projectId, incidentId);
    // markOpsMissionRerun notifies before setRerunBusy flushes — ignore young flags.
    if (age == null || age < 8_000) return;
    clearOpsMissionRerun(projectId, incidentId);
    appendOpsActivity(
      projectId,
      incidentId,
      'Cleared stuck rerun grace — applying natural recovery assessment',
      'system',
    );
  }, [projectId, incidentId, rerunActive, rerunBusy, analyze.isPending]);

  if (!engineeringContext) return null;

  const isRequest = engineeringContext.kind === 'request';
  // Incidents without a code change use one rail driven by the analysis and
  // runbook, whatever their mission type. Requests and code-change incidents
  // follow the engineering pipeline.
  const useIncidentRail = !isRequest && !engineeringOwned && !incidentNeedsEngineering(alertAnalysis, run);
  // Same gate as the Engineering tab's operational empty state. Unanalysed and
  // escalated incidents stay on the engineering rail.
  const operationalNoCode =
    useIncidentRail &&
    !engineeringTabApplies(alertAnalysis, run, engineeringOwned || isEiEscalatedForIncident(projectId, incidentId));

  const missionKind = isRequest ? 'request' : 'incident';
  const stages = useIncidentRail
    ? buildIncidentStages({ analysis: alertAnalysis, runbook: runbookQ.data, closed: incidentClosed })
    : buildMissionHeaderStagesFromHimitsu(
        run?.himitsu,
        run?.state,
        missionKind,
        Boolean(run?.approved_at),
        Boolean(run?.promotion_requested_at),
      );

  const next = missionHeaderNextStage(stages);
  const blocked = stages.some((s) => s.status === 'blocked');
  const progressPct = missionHeaderProgressPercent(stages);
  const incidentSentence = useIncidentRail ? incidentStageSentence(runbookQ.data, incidentClosed) : null;
  const stateChip = useIncidentRail ? (incidentClosed ? 'Closed' : 'Open') : eiStateLabel(run?.state);
  const activity = useIncidentRail
    ? incidentSentence
    : activityLine({ operational: false, eiState: run?.state, toolTrace: run?.tool_trace });
  const eiCue = useIncidentRail ? null : missionHeaderOperatorCue(run?.state, run);
  const humanOrNext =
    useIncidentRail && incidentNeedsYou(runbookQ.data) && !incidentClosed
      ? incidentSentence
      : eiCue?.kind === 'needs_you'
        ? eiCue.message
        : null;
  const softNext = eiCue?.kind === 'next_action' ? eiCue.message : null;
  const blastRadius = buildBlastRadiusVm(run?.implementation_package);
  const patchBlast = blastRadius?.patchScoped ? blastRadius : undefined;
  const riskTier = missionRiskTierWithBlastRadius(
    extractMissionRiskTier(run?.implementation_package?.risk_assessment),
    patchBlast?.riskLevel,
  );
  const blastUnknown = patchBlast?.riskLevel === 'unknown';
  const eiRail = !useIncidentRail;
  // Stall / start-failure cue stays visible while details are collapsed, so a
  // stuck run never reads as "AI is …" with a pulsing badge. Needs-you is
  // already covered by the operator cue.
  const processHeadline = eiRail
    ? buildMissionStatusSummary({
        run,
        status: missionStatusQ.data,
        request: isRequest ? engineeringRequest : null,
      }).headline
    : null;
  const processWarning =
    processHeadline &&
    (processHeadline.tone === 'warning' || processHeadline.tone === 'error') &&
    processHeadline.label !== 'Needs you'
      ? processHeadline
      : null;

  const doneCount = stages.filter((s) => s.status === 'completed').length;

  // Held-up stage colour: amber when it waits on a person, red when it failed.
  const heldIsFailure = eiRail
    ? processHeadline?.tone === 'error'
    : Boolean(runbookQ.data?.steps.some((st) => st.status === 'failed'));

  // One status pill. The EI rail uses the same headline as the Details panel,
  // so the bar and the details can never disagree; ops/manual keep their chip.
  const opsTone: StatusTone = blocked
    ? heldIsFailure
      ? 'error'
      : 'warning'
    : useIncidentRail && incidentClosed
      ? 'success'
      : 'info';
  const pill: { label: string; tone: StatusTone; pulse?: boolean } =
    eiRail && processHeadline
      ? { label: processHeadline.label, tone: processHeadline.tone, pulse: processHeadline.pulse }
      : { label: stateChip, tone: opsTone, pulse: opsTone === 'info' };

  // One sentence: what you must do, else why it is stuck, else what it is doing.
  const sentence = humanOrNext
    ? `Needs you: ${humanOrNext}`
    : processWarning
      ? processWarning.detail
      : activity || softNext || (eiRail ? processHeadline?.detail : null);

  const allDone = stages.length > 0 && doneCount === stages.length;
  const focusStage =
    stages.find((st) => st.status === 'blocked') ?? stages.find((st) => st.status === 'active') ?? next ?? null;
  const headline = allDone
    ? 'All stages complete'
    : focusStage
      ? focusStage.status === 'blocked'
        ? heldIsFailure
          ? `${focusStage.label} failed`
          : `${focusStage.label} is waiting on you`
        : `${focusStage.label} in progress`
      : pill.label;
  const detail = humanOrNext ?? sentence;
  const tone: StatusTone = humanOrNext ? 'warning' : processWarning ? processWarning.tone : pill.tone;
  const StateIcon =
    tone === 'error' ? XCircle : tone === 'warning' ? AlertTriangle : tone === 'success' ? CheckCircle2 : Loader2;
  const spinning = StateIcon === Loader2 && Boolean(pill.pulse);
  const missionLabel = isRequest
    ? 'Engineering request'
    : useIncidentRail
      ? `${classification.mission_type === 'UNCLASSIFIED' ? 'Incident' : `${classification.mission_type.charAt(0)}${classification.mission_type.slice(1).toLowerCase()} incident`}`
      : 'Engineering mission';
  // The runbook lives on the Incidents page; code work on the Engineering tab.
  const remediationHref = incidentId
    ? operateTabPath(projectId, useIncidentRail ? 'incidents' : 'engineering', incidentId)
    : null;
  const primaryLabel = humanOrNext ? 'Respond' : null;
  const rerunning = rerunBusy || analyze.isPending;
  const hasMenu = Boolean(remediationHref);

  // The incident (or request) this mission came from, by its own name.
  const sourceName = (isRequest ? engineeringRequest?.title : incident?.title)?.trim() || null;

  const renderCard = (opts: {
    tone: StatusTone;
    spinning: boolean;
    statePill: { label: string; tone: StatusTone; pulse?: boolean };
    extraTags?: React.ReactNode;
    title: string;
    detail?: string | null;
    primary?: { label: string; onClick: () => void; icon?: React.ReactNode; arrow?: boolean } | null;
    menuItems: React.ReactNode[];
    ariaLabel: string;
  }) => {
    const Icon =
      opts.tone === 'error' ? XCircle : opts.tone === 'warning' ? Clock : opts.tone === 'success' ? CheckCircle2 : opts.spinning ? Loader2 : Clock;
    return (
      <article
        className="mcard"
        style={{ ['--t' as string]: CARD_TONE[opts.tone] }}
        aria-label={opts.ariaLabel}
      >
        <div className="mcard-icon" aria-hidden>
          <Icon size={24} strokeWidth={opts.spinning ? 2.2 : 1.8} className={opts.spinning ? 'mph-spin' : undefined} />
        </div>
        <div className="mcard-body">
          <div className="ops-tags">
            <div ref={flowRef} style={{ display: 'contents' }}>
              <StageFlow
                stages={stages}
                focus={focusStage}
                doneCount={doneCount}
                heldFailure={heldIsFailure}
                open={flowOpen}
                onToggle={() => {
                  setMenuOpen(false);
                  setFlowOpen((o) => !o);
                }}
                onClose={() => setFlowOpen(false)}
                popRef={flowPopRef}
                hrefFor={(id) => stageHref(id, projectId, incidentId, requestId, engineeringContext)}
              />
            </div>
            {opts.extraTags}
          </div>
          <div className="mcard-extra" id="mission-header-body">
            <div>
              <h2 className="ops-a-title">{opts.title}</h2>
              {opts.detail && <p className="ops-a-desc" title={opts.detail}>{opts.detail}</p>}
              {sourceName && (
                <p className="ops-a-incident" title={sourceName}>
                  <Hexagon size={14} strokeWidth={2} aria-hidden />
                  <span className="ops-visually-hidden">{isRequest ? 'Request: ' : 'Incident: '}</span>
                  <span className="ops-a-incident-name">{sourceName}</span>
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="ops-a-actions">
          {opts.primary && (
            <button type="button" className="ops-btn" onClick={opts.primary.onClick}>
              {opts.primary.icon ?? <Upload size={17} strokeWidth={1.9} />}
              {opts.primary.label}
              {opts.primary.arrow && <ArrowRight className="ops-arrow" size={17} strokeWidth={2} />}
            </button>
          )}
          {opts.menuItems.some(Boolean) && <div className="ops-mw" ref={menuRef}>
            <button
              type="button"
              className="ops-btn icon"
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => {
                setFlowOpen(false);
                setMenuOpen((o) => !o);
              }}
            >
              {rerunning ? <EnsMark size="sm" icon={18} /> : <MoreHorizontal size={18} />}
            </button>
            {menuOpen && (
              <div className="ops-menu" role="menu">
                {chrome?.lastUpdatedLabel ? <div className="ops-menu-note">Last updated {chrome.lastUpdatedLabel}</div> : null}
                {opts.menuItems}
              </div>
            )}
          </div>}
        </div>
      </article>
    );
  };

  const menuButton = (
    key: string,
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    disabled?: boolean,
    primary?: boolean,
  ) => (
    <button
      key={key}
      type="button"
      role="menuitem"
      className={primary ? 'primary' : undefined}
      disabled={disabled}
      onClick={() => {
        setMenuOpen(false);
        onClick();
      }}
    >
      {icon}
      {label}
    </button>
  );
  // Refresh / rerun come from the active tab's mission chrome; incidents fall back to re-analysis.
  const refreshItem = chrome?.onRefresh
    ? menuButton(
        'refresh',
        <RotateCw size={16} className={chrome.refreshing ? 'mph-spin' : undefined} />,
        chrome.refreshing ? 'Refreshing…' : 'Refresh',
        chrome.onRefresh,
        chrome.refreshing,
      )
    : null;
  const rerunItem = chrome?.onRerun
    ? menuButton(
        'rerun',
        <Play size={16} fill="currentColor" strokeWidth={0} />,
        chrome.rerunning ? chrome.rerunBusyLabel || 'Running…' : chrome.rerunLabel || 'Rerun mission',
        chrome.onRerun,
        chrome.rerunning || chrome.busy,
        true,
      )
    : incidentId
      ? menuButton(
          'rerun',
          <Play size={16} fill="currentColor" strokeWidth={0} />,
          rerunning ? 'Rerunning…' : 'Rerun mission',
          () => void onRerunMission(),
          rerunning,
          true,
        )
      : null;

  if (operationalNoCode) {
    const operatorStep = pendingOperatorStep(runbookQ.data);
    const statusLine = operatorStep?.title || incidentSentence || 'The analysis points to operational handling.';
    const opsCardTone: StatusTone = operatorStep ? 'warning' : incidentClosed ? 'success' : 'info';
    return (
      <section className="mph-v2" aria-label="Operational incident status">
        {renderCard({
          ariaLabel: 'Operational incident',
          tone: opsCardTone,
          spinning: false,
          statePill: { label: stateChip, tone: opsCardTone },
          extraTags: (
            <>
              {alertAnalysis?.code_change === 'uncertain' ? (
                <span className="ops-pill warn">Code change not determined</span>
              ) : (
                <span className="ops-pill ok">
                  <span className="ops-ico"><Check size={11} strokeWidth={2.4} /></span>
                  No code change required
                </span>
              )}
            </>
          ),
          title: operatorStep ? 'Operational action still open' : 'Operational resolution',
          detail: statusLine,
          primary: remediationHref
            ? {
                label: 'Open the incident',
                icon: <Eye size={17} strokeWidth={1.8} />,
                arrow: true,
                onClick: () => navigate(remediationHref),
              }
            : null,
          menuItems: [refreshItem, rerunItem],
        })}
      </section>
    );
  }

  return (
    <section className="mph-v2" aria-label="Mission progress">
      {renderCard({
        ariaLabel: 'Mission status',
        tone,
        spinning,
        statePill: pill,
        extraTags: (
          <>
            {riskTier && (
              <span className={`ops-pill ${PILL_TONE[RISK_TONE[riskTier]]}`}>
                {`${riskTier.charAt(0).toUpperCase()}${riskTier.slice(1)} risk`}
              </span>
            )}
            {blastUnknown && (
              <span className="ops-pill neutral" title={patchBlast?.riskLabel}>
                Blast radius unknown
              </span>
            )}
          </>
        ),
        title: headline,
        detail,
        primary: primaryLabel && remediationHref ? { label: primaryLabel, onClick: () => navigate(remediationHref) } : null,
        menuItems: [refreshItem, chrome?.onRerun || useIncidentRail ? rerunItem : null],
      })}

    </section>
  );
};

export default MissionProgressHeader;
